import { KeyboardLayoutObject } from 'react-simple-keyboard';

import ComposingState from 'components/Keyboard/cache/composing';
import { SPECIAL_KEYS } from 'components/Keyboard/utils/constants';
import { hasKey } from 'components/Keyboard/utils/KeyUtils';
import { CandidatesKeyboard, KeyboardOptionsExt } from 'components/Keyboard/types';

import { SCREEN_LAYOUTS, SCREEN_KEYS } from './utils';
import { CONVERTER, KEYS_TO_LAYOUT, SHIFT_LAYOUTS } from './utils';
import { ActionHandler } from './types';
import {
  DiacriticMark,
  getDiacriticCharacter,
  getNextCycleCharacter,
  modifiers as diacriticsModifiers,
} from '../layouts/diacritics';

const updateCandidates = (keyboard: CandidatesKeyboard, composing: string, input: string) => {
  ComposingState.set(composing);
  keyboard.updateCandidates(composing);
  keyboard.setInputRange(input);
};

const toggleConvertKey = (keyboard: CandidatesKeyboard, keyToShow: string) => {
  const { layout: currentLayout } = keyboard.getOptions() as KeyboardOptionsExt;
  const updatedLayout: KeyboardLayoutObject = {};

  Object.keys(currentLayout).forEach(layer => {
    updatedLayout[layer] = currentLayout[layer].map(row =>
      row
        .split(' ')
        .map(key => (key === SPECIAL_KEYS.CONVERT || key === SPECIAL_KEYS.CONVERT_HIRAGANA ? keyToShow : key))
        .join(' '),
    );
  });

  keyboard.setOptions({ layout: updatedLayout });
};

export const resetLayout = (keyboard: CandidatesKeyboard) => {
  const { layout, layoutName } = keyboard.getOptions() as KeyboardOptionsExt;
  const currentLayout = layout[layoutName];
  const isKatakanaLayout = layoutName === 'katakana' || layoutName === 'katakanaShift';
  const defaultConvertKey = isKatakanaLayout ? SPECIAL_KEYS.CONVERT_HIRAGANA : SPECIAL_KEYS.CONVERT;
  if (!hasKey(currentLayout, defaultConvertKey)) {
    toggleConvertKey(keyboard, isKatakanaLayout ? SPECIAL_KEYS.CONVERT_HIRAGANA : SPECIAL_KEYS.CONVERT);
  }
};

export const isSpecialAction = (button: string) => Object.keys(BASE_ACTIONS).includes(button);

export const isDiacriticAction = (button: string) => Object.keys(diacriticsModifiers).includes(button);

export const isKatakanaSwitchAction = (button: string) => Object.keys(KEYS_TO_LAYOUT).includes(button);

/**
 * Switch layout from Hiragana あ to Katakana ア
 */
export const toggleKana = (button: string, keyboard: CandidatesKeyboard) => {
  keyboard.setOptions({
    layoutName: KEYS_TO_LAYOUT[button as keyof typeof KEYS_TO_LAYOUT],
  });
  keyboard.refreshCandidates();
};

/**
 * Switch from hiragana to shift hiragana, and from katakana to shift katakana
 */
export const toggleShift = (_: string, keyboard: CandidatesKeyboard) => {
  const nextLayout = SHIFT_LAYOUTS[keyboard.getOptions().layoutName as string] || 'default';
  keyboard.setOptions({ layoutName: nextLayout });
  keyboard.refreshCandidates();

  keyboard[nextLayout !== 'default' ? 'addButtonTheme' : 'removeButtonTheme'](SPECIAL_KEYS.SHIFT, 'active');
};

/**
 * Convert hiragana to katakana and vice versa (i.e あ => ア, か => カ, ア => あ)
 */
export const convertKana = (button: string, keyboard: CandidatesKeyboard) => {
  const initialComposing = ComposingState.get();
  const newValue = CONVERTER[button](initialComposing);

  const currentInput = keyboard.getInput();
  const lastIndex = currentInput.lastIndexOf(initialComposing);
  const newInput =
    currentInput.slice(0, lastIndex) + newValue + currentInput.slice(lastIndex + initialComposing.length);

  keyboard.setInput(newInput);
  keyboard.options.onChange?.(newInput);

  updateCandidates(keyboard, newValue, newInput);
  const newComposing = ComposingState.get();

  if (newComposing !== initialComposing) {
    const nextConvertKey = button === SPECIAL_KEYS.CONVERT ? SPECIAL_KEYS.CONVERT_HIRAGANA : SPECIAL_KEYS.CONVERT;
    toggleConvertKey(keyboard, nextConvertKey);
  }
};

/**
 * Apply diacritic to last character (i.e か => が, は => ば, は => ぱ)
 */
export const applyDiacritic = (button: string, keyboard: CandidatesKeyboard) => {
  const value = keyboard.getInput();
  const newCharacter = getDiacriticCharacter(value, button as DiacriticMark);
  const base = value.slice(0, -1);

  const newValue = newCharacter ? base + newCharacter : value + button;
  keyboard.setInput(newValue);
  keyboard.options.onChange?.(newValue);

  // Requires candidates module
  const composing = ComposingState.get();
  const composingBase = composing.slice(0, -1);
  const newComposing = newCharacter ? composingBase + newCharacter : composing + button;

  updateCandidates(keyboard, newComposing, newValue);
};

export const BASE_ACTIONS: Partial<Record<SPECIAL_KEYS, ActionHandler>> = {
  [SPECIAL_KEYS.SHIFT]: toggleShift,
  [SPECIAL_KEYS.CONVERT]: convertKana,
  [SPECIAL_KEYS.CONVERT_HIRAGANA]: convertKana,
};

export const handleSpecialAction = (button: string, keyboard: CandidatesKeyboard) => {
  BASE_ACTIONS[button as SPECIAL_KEYS]?.(button, keyboard);
};

// --- Zenkaku (全角) full-width toggle ---
export const isZenkakuAction = (button: string) => button === SPECIAL_KEYS.ZENKAKU;

export const getZenkakuMode = (keyboard: CandidatesKeyboard): boolean => {
  return (keyboard.getOptions() as KeyboardOptionsExt).zenkaku ?? false;
};

export const toggleZenkaku = (_button: string, keyboard: CandidatesKeyboard) => {
  const enabled = !(keyboard.getOptions() as KeyboardOptionsExt).zenkaku;
  keyboard.setOptions({ zenkaku: enabled });
  keyboard[enabled ? 'addButtonTheme' : 'removeButtonTheme'](SPECIAL_KEYS.ZENKAKU, 'active');
};

// --- Combined diacritic/small kana cycle (゛゜小 key) ---

export const isCycleModifierAction = (button: string) => button === SPECIAL_KEYS.SMALL;

export const cycleModifier = (_button: string, keyboard: CandidatesKeyboard) => {
  const value = keyboard.getInput();
  const nextChar = getNextCycleCharacter(value);
  if (!nextChar) {
    return;
  }

  const base = value.slice(0, -1);
  const newValue = base + nextChar;
  keyboard.setInput(newValue);
  keyboard.options.onChange?.(newValue);

  const composing = ComposingState.get();
  const composingBase = composing.slice(0, -1);
  const newComposing = composingBase + nextChar;
  updateCandidates(keyboard, newComposing, newValue);
};

// --- Brackets toggle 「」 ---

const BRACKET_PAIR: Record<string, string> = { '「': '」', '」': '「' };

export const isBracketsAction = (button: string) => button === SPECIAL_KEYS.BRACKETS;

export const insertBracket = (_button: string, keyboard: CandidatesKeyboard) => {
  const value = keyboard.getInput();
  const lastChar = value.at(-1);

  // Toggle if last char is already a bracket
  if (lastChar && BRACKET_PAIR[lastChar]) {
    const newValue = value.slice(0, -1) + BRACKET_PAIR[lastChar];
    keyboard.setInput(newValue);
    keyboard.options.onChange?.(newValue);
    return;
  }

  // First press: insert 「
  const newValue = value + '「';
  keyboard.setInput(newValue);
  keyboard.options.onChange?.(newValue);
  keyboard.flushCandidates();
};

// --- Screen navigation ---
export const isScreenSwitchAction = (button: string) => SCREEN_KEYS.has(button);

export const restoreDefaultButtonTheme = (keyboard: CandidatesKeyboard) => {
  Object.values(SPECIAL_KEYS).forEach(key => {
    keyboard.removeButtonTheme(key, 'active');
  });
};

export const switchScreen = (button: string, keyboard: CandidatesKeyboard) => {
  const savedComposing = ComposingState.get();

  keyboard.flushCandidates();

  keyboard.setOptions({
    zenkaku: false,
    layoutName: 'default',
    layout: SCREEN_LAYOUTS[button],
  });

  restoreDefaultButtonTheme(keyboard);

  if (savedComposing) {
    ComposingState.set(savedComposing);
    keyboard.refreshCandidates();
  }
};
